import React, {FC} from 'react';

import {Trans} from '../../../../../libs/shared/core/src/helpers/internationalization/react';
import {Dropdown} from '../../../../../libs/shared/ui/src/components/dropdowns/dropdown';
import {DropdownBanner} from '../../../../../libs/shared/ui/src/components/dropdowns/dropdownBanner';
import {DropdownHeader} from '../../../../../libs/shared/ui/src/components/dropdowns/dropdownHeader';
import {DropdownList} from '../../../../../libs/shared/ui/src/components/dropdowns/dropdownList';
import {SimpleDropdownItem} from '../../../../../libs/shared/ui/src/components/dropdowns/simpleDropdown/simpleDropdownItem';
import {PopoverTransferProps} from '../../../../../libs/shared/ui/src/components/popovers/popoverCoordinator';
import {CrmSyncSourcesEnum} from '../../../../models/api/crmSyncModel';

/*
 * Constants.
 */

const dropdownWidth = 260;

/*
 * Props.
 */

type GenericPreviewResource = {
  id: number;
  name?: string;
};

interface PreviewDropdownProps {
  popoverProps: PopoverTransferProps;
  crmSyncSource: CrmSyncSourcesEnum;
  selectedId?: number;
  resources: ReadonlyArray<GenericPreviewResource>;
  resourceName: string;
  onSelect: (arg: number) => void;
}

/*
 * Component.
 */

export const PreviewDropdown: FC<PreviewDropdownProps> = (props) => {
  const {popoverProps, resources, selectedId, crmSyncSource, resourceName, onSelect} = props;

  const onSelectAccount = (id: number) => {
    onSelect(id);
    popoverProps.onRequestClose();
  };

  return (
    <Dropdown {...popoverProps} width={dropdownWidth} placement="bottom-start">
      <DropdownHeader title={renderDropdownTitle(crmSyncSource, resourceName)} />
      <DropdownBanner>{renderDropdownBanner(crmSyncSource, resourceName)}</DropdownBanner>
      <DropdownList>
        {resources.map((resource) => (
          <SimpleDropdownItem
            key={resource.id}
            onSelect={() => onSelectAccount(resource.id)}
            isChecked={selectedId === resource.id}
          >
            {resource.name}
          </SimpleDropdownItem>
        ))}
      </DropdownList>
    </Dropdown>
  );
};

/*
 * Helpers.
 */

function renderDropdownTitle(crmSyncSource: CrmSyncSourcesEnum, resourceName: string) {
  switch (crmSyncSource) {
    case CrmSyncSourcesEnum.SALESFORCE:
      return <Trans id="crm-preview-salesforce-dropdown-header-title">Salesforce {resourceName}</Trans>;
    case CrmSyncSourcesEnum.HUBSPOT:
      return <Trans id="crm-preview-hubspot-dropdown-header-title">Hubspot {resourceName}</Trans>;
    case CrmSyncSourcesEnum.MICROSOFT_DYNAMICS:
      return (
        <Trans id="crm-preview-microsoft-dynamics-dropdown-header-title">Dynamics 365 {resourceName}</Trans>
      );
    case CrmSyncSourcesEnum.OUTLOOK_DIRECTORY:
      return <Trans id="crm-preview-outlook-dropdown-header-title">Outlook directory {resourceName}</Trans>;
    default:
      return null;
  }
}

function renderDropdownBanner(crmSyncSource: CrmSyncSourcesEnum, resourceName: string) {
  switch (crmSyncSource) {
    case CrmSyncSourcesEnum.SALESFORCE:
      return (
        <Trans id="crm-preview-salesforce-dropdown-banner">
          Only showing {resourceName} recently updated in Salesforce
        </Trans>
      );
    case CrmSyncSourcesEnum.HUBSPOT:
      return (
        <Trans id="crm-preview-hubspot-dropdown-banner">
          Only showing {resourceName} recently updated in Hubspot
        </Trans>
      );
    case CrmSyncSourcesEnum.MICROSOFT_DYNAMICS:
      return (
        <Trans id="crm-preview-microsoft-dynamics-dropdown-banner">
          Only showing {resourceName} recently updated in Dynamics 365
        </Trans>
      );
    case CrmSyncSourcesEnum.OUTLOOK_DIRECTORY:
      return (
        <Trans id="crm-preview-outlook-dropdown-banner">
          Only showing {resourceName} recently updated in Outlook directory
        </Trans>
      );
    default:
      return null;
  }
}
